// /lib/GTMManager.ts
import { trackBackendEvent } from "./eventTracker";

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
        gtag?: (...args: any[]) => void;
    }
}

interface GTMEvent {
    event: string;

    [key: string]: any;
}

export class GTMManager {
    private static instance: GTMManager;
    private gtagId: string | null = null;
    private adsId: string | null = null;
    /**
     * When true, every event pushed to the dataLayer is ALSO delivered to GA4
     * directly via gtag('event', ...). This guarantees events reach GA4 even if
     * no matching GA4 tag is configured in the GTM container. Set to false if you
     * later wire all events through GTM tags (to avoid double counting).
     */
    private directGa4: boolean = true;
    private eventDeduplicationMap: Map<string, number> = new Map(); // Track sent events with timestamps
    // Separate dedup map for the backend delivery leg. It CANNOT share
    // eventDeduplicationMap: that map is only ever written by markEventAsSent,
    // which runs on the gtag-available path below. In ad-blocked sessions
    // window.gtag never becomes truthy, so markEventAsSent never runs — if the
    // backend leg reused that map, its dedup would be a permanent no-op for
    // exactly the audience (ad-blocked users) this leg exists to still reach.
    private backendEventDeduplicationMap: Map<string, number> = new Map();
    private deduplicationWindowMs: number = 1000; // 1 second deduplication window

    private constructor() {
    }

    public static getInstance(): GTMManager {
        if (!GTMManager.instance) {
            GTMManager.instance = new GTMManager();
        }
        return GTMManager.instance;
    }

    /**
     * Initialize gtag.js for GA4 and (optionally) Google Ads.
     * A single gtag.js library is loaded and configured for multiple destinations,
     * which is Google's recommended way to run GA4 + Google Ads together.
     */
    public init(gtagId: string, adsId?: string) {
        if (this.gtagId) return; // already initialized
        this.gtagId = gtagId;

        if (typeof window === "undefined") return;

        window.dataLayer = window.dataLayer || [];

        // Define gtag() if it hasn't already been defined (e.g. by GTM)
        window.gtag = window.gtag || function (...args: any[]) {
            window.dataLayer.push(args);
        };

        // Load gtag.js once (covers both GA4 and Google Ads destinations)
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
        document.head.appendChild(script);

        // Init GA4
        window.gtag("js", new Date());
        window.gtag("config", gtagId);

        // Init Google Ads global site tag (installed on every page)
        if (adsId) {
            this.adsId = adsId;
            window.gtag("config", adsId);
        }
    }

    /** Enable/disable direct GA4 delivery via gtag('event', ...). */
    public setDirectGa4(enabled: boolean): void {
        this.directGa4 = enabled;
    }

    /** Generate a unique key for deduplication */
    private generateEventKey(event: GTMEvent): string {
        const context = [
            event.search_term,
            event.filter_name,
            event.filter_value,
            event.transaction_id,
            event.method,
        ].filter(Boolean).join('|');
        return `${event.event}_${context}_${JSON.stringify(event.ecommerce || {})}`
    }

    /** Check if event was already sent recently */
    private isDuplicateEvent(event: GTMEvent): boolean {
        const key = this.generateEventKey(event);
        const lastSentTime = this.eventDeduplicationMap.get(key);

        if (!lastSentTime) return false;

        const timeSinceLastSent = Date.now() - lastSentTime;
        return timeSinceLastSent < this.deduplicationWindowMs;
    }

    /** Mark event as sent */
    private markEventAsSent(event: GTMEvent): void {
        const key = this.generateEventKey(event);
        this.eventDeduplicationMap.set(key, Date.now());
    }

    /** Check if event was already sent to the backend leg recently (independent of gtag delivery). */
    private isDuplicateBackendEvent(event: GTMEvent): boolean {
        const key = this.generateEventKey(event);
        const lastSentTime = this.backendEventDeduplicationMap.get(key);

        if (!lastSentTime) return false;

        const timeSinceLastSent = Date.now() - lastSentTime;
        return timeSinceLastSent < this.deduplicationWindowMs;
    }

    /** Mark event as sent to the backend leg. */
    private markBackendEventAsSent(event: GTMEvent): void {
        const key = this.generateEventKey(event);
        this.backendEventDeduplicationMap.set(key, Date.now());
    }

    /** Push custom GA4 event */
    public pushEvent(event: GTMEvent) {
        if (typeof window === "undefined") return;

        // Backend delivery leg (admin behavioral tracking). Fires even when gtag
        // is blocked (ad-blockers), using its own dedup map (see comment above)
        // so it isn't a permanent no-op in exactly that scenario. Wrapped in a
        // try/catch: pushEvent is a shared chokepoint for ~40 call sites, and a
        // bug in the batching/queue code below must never break GTM/GA delivery.
        if (!this.isDuplicateBackendEvent(event)) {
            try {
                trackBackendEvent(event);
                this.markBackendEventAsSent(event);
            } catch (err) {
                console.error("[GTM] trackBackendEvent failed:", err);
            }
        }

        // --- gtag / dataLayer path below is unchanged from before ---
        if (!window.gtag) return console.warn("gtag not initialized!");

        // Skip duplicate events
        if (this.isDuplicateEvent(event)) {
            console.warn(`[GTM] Skipped duplicate event: ${event.event}`);
            return;
        }

        this.markEventAsSent(event);

        // Clear the previous ecommerce object so item-scoped data from a prior
        // event doesn't bleed into this one (GA4 ecommerce best practice).
        if (event.ecommerce) {
            window.dataLayer.push({ ecommerce: null });
        }

        // 1) Push to the dataLayer for GTM (existing behavior — tags in the
        //    GTM container can consume this).
        console.log("Sending GTM event:", event);
        window.dataLayer.push({ ...event });

        // 2) Also deliver to GA4 directly via gtag so the event is captured even
        //    when no matching GA4 tag exists in the GTM container. `send_to`
        //    scopes it to GA4 only (it is NOT sent to Google Ads).
        if (this.directGa4 && this.gtagId && window.gtag) {
            const { event: name, ecommerce, ...rest } = event;
            const params: Record<string, any> = {
                ...rest,
                ...(ecommerce || {}),
                send_to: this.gtagId,
            };
            window.gtag("event", name, params);
        }
    }

    /** Clear deduplication map (useful for testing) */
    public clearDeduplicationMap(): void {
        this.eventDeduplicationMap.clear();
    }

    /** Set custom deduplication window (in milliseconds) */
    public setDeduplicationWindow(ms: number): void {
        this.deduplicationWindowMs = ms;
    }
}
